import clsx from "clsx";
import { useRouter } from "next/router";
import Link from "next/link";

import { BRAND_LOGGED_IN_START_PAGE, BRAND_PATHS } from "@chef/constants";
import { getNextCutoff } from "@chef/helpers";
import { isEqualStrings } from "@chef/utils/equal";
import { Breakfast, Plate, Cart } from "@chef/icons/large";
import { DeviationIndicator } from "@chef/smart-components";
import { useViewport } from "@chef/hooks";

import { Logo } from "../Logo";
import { DrawerButton } from "./Drawer/DrawerButton";
import { useSticky } from "../Sticky";
import { WeeksNavigation } from "./WeeksNavigation";

import { intl } from "./MyMenuVariant.Intl";

interface LinkWithIconProps {
  isActive: boolean;
  href: string;
  label: string;
  children: React.ReactNode;
  id: string;
  className?: string;
}

const LinkWithIcon = ({
  isActive,
  href,
  label,
  children,
  id,
}: LinkWithIconProps) => (
  <Link
    className="relative flex items-center gap-2 lg:gap-4 group"
    href={href}
    id={id}
  >
    {children}
    <span
      className={clsx(
        "group-hover:underline decoration-2 underline-offset-8 decoration-primary",
        isActive && "underline",
      )}
    >
      {label}
    </span>
  </Link>
);

export const MyMenuVariant = () => {
  const router = useRouter();

  const { isFloating } = useSticky();

  const [breakpoint] = useViewport();
  const isDesktop = breakpoint !== "sm";

  const nextCutoff = getNextCutoff();

  const week = +(router.query["week"] ?? nextCutoff.week);
  const year = +(router.query["year"] ?? nextCutoff.year);

  const isFloatingAndDesktop = isFloating && isDesktop;
  const isNotFloatingAndDesktop = !isFloating && isDesktop;

  return (
    <div className="flex-col gap-2">
      <div
        id="my-menu-header"
        className="relative flex items-center justify-between w-full h-15"
      >
        <div className="flex w-24 md:w-48">
          {isFloatingAndDesktop ? (
            <nav className="flex items-center gap-7 shrink-0">
              <LinkWithIcon
                href={BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year })}
                label={intl.MEALS}
                id="header-nav-dishes"
                isActive={[
                  BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year }),
                  BRAND_PATHS.MY_MENU_PAGE,
                ].some((link) => isEqualStrings(router.asPath, link))}
              >
                <Plate className="w-6 h-6" />
              </LinkWithIcon>

              <LinkWithIcon
                href={BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year })}
                label={intl.ADDONS}
                id="header-nav-addons"
                isActive={isEqualStrings(
                  router.asPath,
                  BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }),
                )}
              >
                <Breakfast className="w-6 h-6" />
              </LinkWithIcon>
            </nav>
          ) : (
            <Logo responsive href={BRAND_LOGGED_IN_START_PAGE} />
          )}
        </div>

        <WeeksNavigation variant="my-menu" />
        <div className="flex items-center justify-end w-24 gap-4 md:w-48">
          {isFloatingAndDesktop && (
            <LinkWithIcon
              className="relative"
              href={BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({
                week,
                year,
              })}
              label={intl.ORDER}
              id="header_nav_checkout"
              isActive={[
                BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year }),
                BRAND_PATHS.WEEK_EDITOR_CONFIRMATION_PAGE({ week, year }),
                BRAND_PATHS.WEEK_EDITOR_ADD_MORE_PAGE({ week, year }),
              ].some((link) => isEqualStrings(router.asPath, link))}
            >
              <DeviationIndicator
                week={week}
                year={year}
                className="absolute left-4 -top-2"
              />
              <Cart className="w-6 h-6" />
            </LinkWithIcon>
          )}

          <DrawerButton />
        </div>
      </div>

      {isNotFloatingAndDesktop && (
        <div className="flex items-center justify-between pb-4 h-15">
          <div className="flex gap-7">
            <LinkWithIcon
              href={BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year })}
              label={intl.MEALS}
              id="header-nav-dishes"
              isActive={[
                BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year }),
                BRAND_PATHS.MY_MENU_PAGE,
              ].some((link) => isEqualStrings(router.asPath, link))}
            >
              <Plate className="w-6 h-6" />
            </LinkWithIcon>

            <LinkWithIcon
              href={BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year })}
              label={intl.ADDONS}
              id="header-nav-addons"
              isActive={isEqualStrings(
                router.asPath,
                BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }),
              )}
            >
              <Breakfast className="w-6 h-6" />
            </LinkWithIcon>
          </div>
          <LinkWithIcon
            className="relative mr-2 lg:mr-4"
            href={BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year })}
            label={intl.ORDER}
            id="header_nav_checkout"
            isActive={[
              BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year }),
              BRAND_PATHS.WEEK_EDITOR_CONFIRMATION_PAGE({ week, year }),
              BRAND_PATHS.WEEK_EDITOR_ADD_MORE_PAGE({ week, year }),
            ].some((link) => isEqualStrings(router.asPath, link))}
          >
            <DeviationIndicator
              week={week}
              year={year}
              className="absolute left-4 -top-2"
            />
            <Cart className="w-6 h-6" />
          </LinkWithIcon>
        </div>
      )}
    </div>
  );
};
